import type { PostWithTargets } from '@postpilot/core/domain';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { usePostsStore } from '@/stores/posts-store';
import { EditPostDialog } from './edit-post-dialog';
import { QueueFilters } from './queue-filters';
import { QueueTable } from './queue-table';

export function QueuePage() {
  const navigate = useNavigate();

  const total = usePostsStore((s) => s.total);
  const filters = usePostsStore((s) => s.filters);
  const setFilters = usePostsStore((s) => s.setFilters);
  const fetch = usePostsStore((s) => s.fetch);

  const [editing, setEditing] = React.useState<PostWithTargets | null>(null);

  React.useEffect(() => {
    void fetch();
  }, [fetch]);

  const totalPages = Math.max(1, Math.ceil(total / filters.pageSize));
  const from = total === 0 ? 0 : (filters.page - 1) * filters.pageSize + 1;
  const to = Math.min(filters.page * filters.pageSize, total);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Queue</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Everything scheduled, publishing, published or failed — with live status per account.
          </p>
        </div>

        <Button onClick={() => navigate('/compose')}>New post</Button>
      </div>

      <QueueFilters />

      <QueueTable onEdit={setEditing} />

      {total > filters.pageSize && (
        <div className="flex items-center justify-between">
          <p className="text-muted-foreground text-xs">
            {from}–{to} of {total}
          </p>

          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              disabled={filters.page <= 1}
              onClick={() => setFilters({ page: filters.page - 1 })}
            >
              <ChevronLeft />
              Previous
            </Button>

            <span className="text-muted-foreground px-3 text-xs">
              Page {filters.page} of {totalPages}
            </span>

            <Button
              variant="outline"
              size="sm"
              disabled={filters.page >= totalPages}
              onClick={() => setFilters({ page: filters.page + 1 })}
            >
              Next
              <ChevronRight />
            </Button>
          </div>
        </div>
      )}

      <EditPostDialog post={editing} onClose={() => setEditing(null)} />
    </div>
  );
}
